import DataTable from "@components/DataTable";
import useListVendorsForAccount from "./api";
import { Vendor } from "./types";
import { Button, Card, Form } from "react-bootstrap";
import { useState } from "react";
import { ColumnDef } from "@tanstack/react-table";
import { useDeleteVendorMutation } from "@generated";
import { useSelector } from "react-redux";
import { RootState } from "@reducers/rootReducer";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrashCan } from "@fortawesome/pro-solid-svg-icons";
import ConfirmationModal from "@components/Modals/ConfirmationModal";
import FormFieldLabel from "@components/Forms/FormFieldLabel/FormFieldLabel";
import SearchField from "@components/Forms/SearchField/SearchField";
import { useNavigate } from "react-router-dom";
import SlideOutMenuSectioned from "@components/SlideOutMenu/SlideOutMenuSectioned";

const Show = (): JSX.Element => {
  const navigate = useNavigate();
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [vendorToSearch, setVendorToSearch] = useState("");
  const [filter, setFilter] = useState<{ name?: string }>({});
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const { accountSlug } = useSelector((state: RootState) => state.slug);
  const [deleteVendor, {}] = useDeleteVendorMutation();
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [slideOutContent, setSlideOutContent] = useState<{
    title: string;
    content: any[];
  }>({ title: "", content: [] });

  const { vendors, pageMeta, isLoading, refetch } = useListVendorsForAccount({
    page: {
      number: pageNumber,
      size: pageSize,
    },
    filter,
  });

  const handleDelete = () => {
    setShowConfirmationModal(true);
  };

  const handleConfirm = () => {
    selectedIds.forEach((vendorId) => {
      deleteVendor({ slug: accountSlug, id: parseInt(vendorId) });
    });
    setSelectedIds([]);
    refetch();
    setShowConfirmationModal(false);
  };

  const handleCancel = () => {
    setShowConfirmationModal(false);
  };

  const searchVendorName = (vendorName: string) => {
    setFilter(vendorName ? { name: vendorName } : {});
  };

  const handleVendorSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    searchVendorName(vendorToSearch);
  };

  const columns: ColumnDef<Vendor>[] = [
    {
      header: "Name",
      cell(props) {
        return (
          <div
            className="vendorName"
            onClick={() => {
              localStorage.setItem("lastPage", window.location.href);
              navigate(`edit/${props.row.original.id}`);
            }}
          >
            {props.row.original.name}
          </div>
        );
      },
    },
    {
      header: "Address",
      cell(props) {
        const { streetAddress, street2, city, state, postalCode } =
          props.row.original;
        const line1 = [streetAddress, street2].filter(Boolean).join(", ");
        const line2 = [city, state].filter(Boolean).join(", ");
        const full = [line1, line2, postalCode].filter(Boolean).join(", ");
        return <div>{full}</div>;
      },
    },
    {
      header: "Number of Projects",
      cell(props) {
        return <div>{props.row.original.numProjects || ""}</div>;
      },
    },
  ];

  if (isLoading) {
    return <ScopeStackSpinner />;
  }

  interface Column {
    header: string;
    cell: (props: any) => JSX.Element;
  }

  const getSlideOutTable = (type, data) => {
    const column: Column[] = [
      {
        header: type,
        cell(props) {
          return props.row.original.name;
        },
      },
    ];

    return [
      {
        sectionTitle: "",
        sectionClass: "",
        sectionContent: (
          <aside>
            <DataTable data={data} columns={column} striped />
          </aside>
        ),
      },
    ];
  };

  return (
    <div className="vendorsTable">
      <SlideOutMenuSectioned
        show={isOpen}
        setShow={setIsOpen}
        menuHeader={`All Assigned ${slideOutContent.title}`}
        actionButtonText={null}
        onActionButtonClick={() => setIsOpen(false)}
        clearFields={null}
        onCancel={() => setIsOpen(false)}
        onClearFieldsClick={() => null}
        sectionsArray={slideOutContent.content}
      />
      <Card
        style={{
          padding: "10px",
          borderRadius: "4px",
          border: "1px solid var(--gray-3-stroke, #D7D7D7)",
          background: " var(--white, #FFF)",
          boxShadow: "0px 2px 3px 0px rgba(0, 0, 0, 0.15)",
        }}
      >
        <Card.Header
          style={{
            backgroundColor: "#fff",
            lineHeight: "unset",
            display: "block",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <div className="accordianTitle" style={{ margin: "0" }}>
              Vendors
            </div>
            <Button
              className="squareGreenButton"
              onClick={() => navigate(`create`)}
            >
              Add A Vendor
            </Button>
          </div>
          <hr></hr>
          <div
            style={{
              display: "flex",
            }}
          >
            {selectedIds.length > 0 && (
              <Button
                style={{
                  marginRight: "10px",
                  alignSelf: "flex-end",
                  background: "unset",
                  color: "var(--teal-1, #418172)",
                  fontWeight: "700",
                  width: "11em",
                  height: "40px",
                  borderRadius: "4px",
                  border: "1px solid var(--gray-3-stroke, #D7D7D7)",
                }}
                onClick={handleDelete}
              >
                {selectedIds.length} Selected{" "}
                <span
                  style={{
                    borderLeft: "1px solid #D7D7D7",
                    height: "24px",
                    margin: "0 8px",
                  }}
                ></span>
                <FontAwesomeIcon style={{ color: "black" }} icon={faTrashCan} />
              </Button>
            )}
            <div style={{ width: "-webkit-fill-available" }}>
              <FormFieldLabel label="Search Vendors" className={undefined} />
              <Form
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleVendorSearch(e);
                  }
                }}
                onSubmit={handleVendorSearch}
              >
                <SearchField
                  placeholder="Search by name"
                  onChange={(e) => {
                    setPageNumber(1);
                    setVendorToSearch(e.target.value);
                  }}
                  value={vendorToSearch}
                  onClick={() => {
                    setVendorToSearch("");
                    searchVendorName("");
                  }}
                  fullWidth
                />
              </Form>
            </div>
          </div>
        </Card.Header>
        <DataTable
          data={vendors}
          columns={columns}
          selectable
          bordered
          striped
          hover
          totalPages={pageMeta.pageCount}
          totalRows={pageMeta.recordCount}
          currentPage={pageNumber}
          setCurrentPage={setPageNumber}
          pageSize={pageSize}
          onPageSizeChange={setPageSize}
          selectedIds={selectedIds}
          setSelectedIds={setSelectedIds}
          paginationEnabled
          pageSizeEnabled
        />
      </Card>
      <ConfirmationModal
        title="Confirm Delete"
        message={`Are you sure that you want to delete ${selectedIds.length} vendors?`}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
        show={showConfirmationModal}
        onHide={handleCancel}
      />
    </div>
  );
};

export default Show;
